import { Vector2 } from "../../../common/vector2.js";
import type { SurfaceAppearMotion } from "../../dom_backend/surface_motion.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../transforms/transform_opacity.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformRotate } from "../../../transforms/transform_rotate.js";
import { setButtonTextOutline } from "../../common_ui_elements/button_text_outline.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { goalIconFor } from "../goal_icon.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";
import { CoinFlightHud } from "../coin_flight_hud.js";
import { Vfx2DManager } from "../../../webgl_2d/vfx_2d_manager.js";
import { WebGLUIRuntime } from "../../../webgl_2d/webgl_ui_runtime.js";
import { Vfx2DRuntime } from "../../../webgl_2d/vfx_2d_runtime.js";
import { FireworksColor } from "../../../webgl_2d/vfx/fireworks/fireworks_types.js";
import { MoveDirection } from "../../../common/types.js";
import { Game } from "../../../game.js";
import { showRewarded } from "../../../ads/rewarded.js";
import { tryShowInterstitial } from "../../../ads/interstitial.js";
import { preloadInterstitialInBackground } from "../../../ads/first_load_manager.js";
import { AdsConfigService } from "../../../ads/ads_config.js";
import { PlayDialogDifficulty } from "../../dialog/play_dialog.js";
import { getLevelDifficulty } from "../../../level_config.js";
import { LevelPackService } from "../../../level_pack/level_pack_service.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import { IController } from "../../../webgl_2d/core/types/controller_types.js";
import { hapticsController, VibrationContext } from "../../../haptics/haptics_controller.js";
import { HapticPattern } from "../../../haptics/haptics_constants.js";
import { TransformValue } from "../../../transforms/transform_value.js";
import { AdventureRewardType, ADVENTURE_REWARD_ICONS } from "../../../adventure/adventure_box_rewards.js";
import { isTimedRewardType, rewardAmountText } from "../../../common/reward_format.js";
import { DAILY_CHALLENGE_DAYS_COUNT } from "../../../daily_challenge/daily_challenge_config.js";
import { AdventureRewardHud, AdventureRewardHudRefs } from "../../screens/adventure_screen/adventure_reward_hud.js";
import type { WinStreakTowerProgressDto } from "../../../api/dto/win_streak_tower.dto.js";
import { WinStreakTowerService } from "../../../win_streak_tower/win_streak_tower_service.js";
import { TournamentService } from "../../../tournament/tournament_service.js";
import { RewardFrame } from "../../common_ui_elements/reward_frame.js";
import { RewardTooltip } from "../../tooltip/reward_tooltip.js";
import { areAdsBlockedOffline } from "../../../ads/offline_ads.js";
import { hideOfflineAdTooltip, showAdUnavailableTooltip, showOfflineAdTooltip } from "../../tooltip/offline_ad_tooltip.js";

export type ResultRewardCurrency = AdventureRewardType;

export interface ResultReward
{
  currency: ResultRewardCurrency;
  amount: number;
}

export interface GameResultOverlayParams
{
  level?: number;
  difficulty?: PlayDialogDifficulty;
  title_key?: string;
  stars?: number;
  rewards?: ResultReward[];
  reward_title_key?: string;
  rewarded_level?: number;
  onContinue?: () => void;
  // Returns the actual coin bonus granted by the server, so the popup can
  // display the real doubled total instead of assuming amount * 2.
  onDoubleReward?: ( rewards: ResultReward[] ) => ( number | void ) | Promise<number | void>;

  daily_challenge_num?: number;
}

const DESIGN_W = 1080;
const DESIGN_H = 1920;
const DESIGN_CX = DESIGN_W / 2;
const DESIGN_CY = DESIGN_H / 2;

const RESULT_SHADOW_COLOR = "rgba(16, 6, 84, 0.43)";
const RESULT_BTN_OUTLINE_W = 8;
const RESULT_BTN_OUTLINE_OFFSET_Y = 2;

interface Box { x: number; y: number; w: number; h: number; cx: number; }

export class GameResultOverlay extends AbstractOverlay
{
  private static readonly APPEAR_MS = 240;
  private static readonly ADVENTURE_FILL_DELAY_MS = 300;
  private static readonly ADVENTURE_FILL_MS = 500;
  private static readonly COIN_FLY_DELAY_MS = 180;
  private static readonly COIN_OVERLAY_CLOSE_DELAY_MS = 320;

  // How long the win overlay stays up after Claim. The coins leave the reward
  // icon at COIN_FLY_DELAY_MS, so this has to be comfortably longer than that
  // or the overlay is gone before the flight reads as having started here —
  // at 320ms only ~140ms of it was ever seen on this screen. The rest of the
  // flight finishes over whatever comes next (the loading screen, or the menu).
  private static readonly COIN_OVERLAY_HOLD_MS = 600;
  private static readonly HEADER_OFFSET_Y_DESIGN = 0;
  private static readonly TOURNAMENT_CONTENT_RISE_DESIGN = 45;
  private static readonly RESULT_TEXT_ARC_RADIUS_DESIGN = 2200;

  // Exact transforms from the 1080x1920 design artboard.
  private static readonly STAR_D_DESIGN = 226;
  private static readonly LEFT_STAR_X_DESIGN = 226.61;
  private static readonly LEFT_STAR_Y_DESIGN = 194.15;
  private static readonly MIDDLE_STAR_X_DESIGN = 433;
  private static readonly MIDDLE_STAR_Y_DESIGN = 154.99;
  private static readonly RIGHT_STAR_X_DESIGN = 632.39;
  private static readonly RIGHT_STAR_Y_DESIGN = 202.15;

  private static readonly STAR_BASE_DELAY_MS = 166;
  private static readonly STAR_STAGGER_MS = 122;
  private static readonly STAR_POP_MS = 154;
  private static readonly STAR_SETTLE_MS = 122;
  private static readonly STAR_POP_SCALE = 1.28;
  private static readonly STAR_START_SCALE = 0.3;
  private static readonly STAR_SPIN_DEG = 32;

  private static readonly FIREWORKS_COLORS: ReadonlyArray<FireworksColor> = [
    FireworksColor.Orange,
    FireworksColor.Red,
    FireworksColor.Green,
    FireworksColor.Blue,
    FireworksColor.Purple,
  ];

  private static readonly FIREWORKS_SERIES_COUNT     : number = 3;
  private static readonly FIREWORKS_SHOTS_PER_SERIES : number = 4;
  private static readonly FIREWORKS_SERIES_DELAY     : number = 800;
  private static readonly FIREWORKS_BASE_Y_RATIO     : number = 0.7;
  private static readonly FIREWORKS_OFFSET_X_MIN     : number = -300;
  private static readonly FIREWORKS_OFFSET_X_RANGE   : number = 600;
  private static readonly FIREWORKS_OFFSET_Y_RANGE   : number = 200;
  private static readonly FIREWORKS_HEIGHT_MIN       : number = 400;
  private static readonly FIREWORKS_HEIGHT_RANGE     : number = 750;
  private static readonly FIREWORKS_DRIFT_X_MIN      : number = -50;
  private static readonly FIREWORKS_DRIFT_X_RANGE    : number = 100;
  private static readonly FIREWORKS_RANDOM_DELAY_MAX : number = 400;
  private static readonly FIREWORKS_EXPLOSION_DELAY  : number = 700;

  private interactive_at = 0;
  private continued = false;
  private reward_inflight = false;
  private reward_elements: UIElement[] = [];
  private is_doubled = false;
  private reward_title_text?: UITextElement;
  private reward_amount_texts: UITextElement[] = [];
  private claim_button?: UINineSliceElement;
  private claim_button_text?: UITextElement;
  private video_button_text?: UITextElement;
  private video_button?: UINineSliceElement;
  private shown_ads_offline = false;
  private reward_glow_controller: IController | null = null;
  private is_closed: boolean = false;
  private reward_finish_at = 0;
  private keep_coin_flight_on_close = false;
  private fireworks_timeout_ids: number[] = [];
  private fireworks_stopped = true;
  private adventure_progress_bg?: UIImage;
  private adventure_progress_fill?: UIImage;
  private adventure_progress_top_text?: UITextElement;
  private adventure_progress_bottom_text?: UITextElement;

  /**
   * Did finishing this level earn the next block? The block appears AT its
   * unlock level, so it is earned by clearing the one before it — which is
   * exactly when the bar reads full.
   *
   * No schedule means no block to celebrate: the congratulation is silent
   * rather than generic.
   */
  private static goalJustEarned( completed_level: number ): boolean
  {
    return LevelPackService.isScheduledLevel( completed_level + 1 );
  }

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: GameResultOverlayParams,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );

    preloadInterstitialInBackground();
    this.createBanner();
    this.createAdventureProgress();
    this.createStars();
    this.createResultText();
    this.createReward();
    this.createGoalProgress();
    this.createRewardButtons();
    this.startAppearAnimation();

    this.interactive_at = performance.now() + GameResultOverlay.APPEAR_MS * 0.6;

    if ( params?.stars && params.stars >= 1 )
    {
      const difficulty = params.difficulty ?? ( params.level !== undefined ? getLevelDifficulty( params.level ) : PlayDialogDifficulty.Common );
      if ( difficulty === PlayDialogDifficulty.Common )
      {
        Vfx2DManager.addConfettiBottom( performance.now() );
      }
      else
      {
        this.addFireworks();
        Vfx2DManager.addConfettiSide( performance.now() );
      }
    }
  }

  private addFireworks(): void
  {
    this.fireworks_stopped = false;
    const screen_w = Game.getScreenWidth();
    const screen_h = Game.getScreenHeight();
    const base = new Vector2( screen_w / 2, screen_h * GameResultOverlay.FIREWORKS_BASE_Y_RATIO );

    for ( let s = 0; s < GameResultOverlay.FIREWORKS_SERIES_COUNT; s++ )
    {
      const series_ms = s * GameResultOverlay.FIREWORKS_SERIES_DELAY;
      for ( let i = 0; i < GameResultOverlay.FIREWORKS_SHOTS_PER_SERIES; i++ )
      {
        const offset_x = LayoutScaler.widthFromDesign( GameResultOverlay.FIREWORKS_OFFSET_X_MIN + Math.random() * GameResultOverlay.FIREWORKS_OFFSET_X_RANGE );
        const offset_y = LayoutScaler.widthFromDesign( Math.random() * GameResultOverlay.FIREWORKS_OFFSET_Y_RANGE );
        const height = LayoutScaler.widthFromDesign( GameResultOverlay.FIREWORKS_HEIGHT_MIN + Math.random() * GameResultOverlay.FIREWORKS_HEIGHT_RANGE );

        const color = GameResultOverlay.FIREWORKS_COLORS[Math.floor( Math.random() * GameResultOverlay.FIREWORKS_COLORS.length )];
        const dir = Math.random() > 0.5 ? MoveDirection.Forward : MoveDirection.Backward;
        const delay = series_ms + Math.random() * GameResultOverlay.FIREWORKS_RANDOM_DELAY_MAX;

        const timeout_id = window.setTimeout( () =>
        {
          if ( this.fireworks_stopped )
            return;

          const start = new Vector2( base.x + offset_x, base.y + offset_y );
          const end = new Vector2(
            start.x + LayoutScaler.widthFromDesign( GameResultOverlay.FIREWORKS_DRIFT_X_MIN + Math.random() * GameResultOverlay.FIREWORKS_DRIFT_X_RANGE ),
            start.y - height
          );
          const now = performance.now();
          Vfx2DManager.addFireworksTrail( start, end, color, dir, now );
          Vfx2DManager.addFireworksExplosion( end, color, now + GameResultOverlay.FIREWORKS_EXPLOSION_DELAY );
        }, delay );
        this.fireworks_timeout_ids.push( timeout_id );
      }
    }
  }

  private stopFireworks(): void
  {
    if ( this.fireworks_stopped )
      return;

    this.fireworks_stopped = true;
    for ( const timeout_id of this.fireworks_timeout_ids )
      window.clearTimeout( timeout_id );
    this.fireworks_timeout_ids.length = 0;
    Vfx2DManager.clearFireworks();
  }

  private box( dx: number, dy: number, dw: number, dh: number ): Box
  {
    const w = LayoutScaler.widthFromDesign( dw );
    const h = LayoutScaler.heightFromDesign( dh );
    const cx = this.rect.getW2() + LayoutScaler.widthFromDesign( ( dx + dw / 2 ) - DESIGN_CX );
    const rise = this.isTournamentResult() ? GameResultOverlay.TOURNAMENT_CONTENT_RISE_DESIGN : 0;
    const cy = this.rect.getH2() + LayoutScaler.heightFromDesign( ( dy + dh / 2 ) - DESIGN_CY - rise );
    return { x: cx - w / 2, y: cy - h / 2, w, h, cx };
  }

  private isTournamentResult(): boolean
  {
    const params = this.params as GameResultOverlayParams;
    return params.daily_challenge_num === undefined && ( params.stars ?? 0 ) > 0
      && TournamentService.isActive() && !TournamentService.isFinished();
  }

  private createBanner(): void
  {
    const params = this.params as GameResultOverlayParams;
    if ( params.daily_challenge_num !== undefined )
    {
      this.createDailyChallengeBanner( params.daily_challenge_num );
      return;
    }

    const b = this.box(
      22,
      141 + GameResultOverlay.HEADER_OFFSET_Y_DESIGN,
      1036,
      503
    );
    const difficulty = this.resultDifficulty();
    const banner_img_id =
      difficulty === PlayDialogDifficulty.SuperHard ? ImgID.UI_ResultBannerSuperHard
      : difficulty === PlayDialogDifficulty.Hard ? ImgID.UI_ResultBannerHard
      : ImgID.UI_ResultBannerCommon;

    const banner = new UIImage( this.ctx, b.x, b.y, b.w, b.h, banner_img_id );
    this.addChild( banner );
  }

  private resultDifficulty(): PlayDialogDifficulty
  {
    const params = this.params as GameResultOverlayParams;
    return params.difficulty
      ?? ( params.level !== undefined
        ? getLevelDifficulty( params.level )
        : PlayDialogDifficulty.Common );
  }

  private resultTitleOutlineColor( is_challenge: boolean ): string
  {
    if ( is_challenge ) return ColorScheme.result_title_outline_challenge;

    const difficulty = this.resultDifficulty();
    if ( difficulty === PlayDialogDifficulty.SuperHard ) return ColorScheme.result_title_outline_super_hard;
    if ( difficulty === PlayDialogDifficulty.Hard ) return ColorScheme.result_title_outline_hard;
    return ColorScheme.result_title_outline_common;
  }

  private createDailyChallengeBanner( day: number ): void
  {
    const HEADER_W_DESIGN = 660;
    const HEADER_H_DESIGN = 252;
    const b = this.box( ( DESIGN_W - HEADER_W_DESIGN ) / 2, 240, HEADER_W_DESIGN, HEADER_H_DESIGN );

    const bg = new UIImage( this.ctx, b.x, b.y, b.w, b.h, ImgID.UI_GameDailyChallengeHeader );
    this.addChild( bg );

    const text_w = LayoutScaler.widthFromDesign( 80 );
    const text_h = LayoutScaler.heightFromDesign( 70 );
    const text_y = LayoutScaler.yFromDesign( 174 );
    const text_x = ( b.w - text_w ) / 2;
    const day_label = `${day}/${DAILY_CHALLENGE_DAYS_COUNT}`;

    const text = new UITextElement(
      this.ctx, text_x, text_y, text_w, text_h,
      {
        text: day_label,
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        font_weight: 700,
        text_color: ColorScheme.white,
        vertical_align: MyTextVerticalAlign.Center,
      }
    );

    const text_outline = new UITextElement(
      this.ctx, text_x, text_y, text_w, text_h,
      {
        text: day_label,
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        font_weight: 700,
        text_color: ColorScheme.white,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_color: ColorScheme.menu_top_el_text,
      }
    );

    bg.addChild( text );
    bg.addChild( text_outline );
  }

  private createAdventureProgress()
  {
    const params = this.params as GameResultOverlayParams;
    if ( params.daily_challenge_num !== undefined )
      return;

    const b = this.box(
      846,
      552 + GameResultOverlay.HEADER_OFFSET_Y_DESIGN,
      207,
      633
    );

    const bg = new UIImage( this.ctx, b.x, b.y, b.w, b.h, ImgID.UI_GameAdventureProgressBg );
    bg.setOpacityImmediate( 0 );
    this.addChild( bg );
    this.adventure_progress_bg = bg;

    // Keep the replacement art at its native design size so its keyhole
    // silhouette lines up with the matching recess in the tower background.
    const fill_w = LayoutScaler.widthFromDesign( 100 );
    const fill_h = LayoutScaler.heightFromDesign( 400 );
    const fill_x = b.w / 2 - fill_w / 2;
    // The circular recesses in the new background are centered at y=255/555;
    // placing the 100px end caps at y=205 centers both of them exactly.
    const fill_y = LayoutScaler.yFromDesign( 205 );

    const fill = new UIImage( this.ctx, fill_x, fill_y, fill_w, fill_h, ImgID.UI_GameAdventureProgressFill );
    fill.setRevealFromBottom( 0 );
    bg.addChild( fill );
    this.adventure_progress_fill = fill;

    const txt_w = LayoutScaler.widthFromDesign( 92 );
    const txt_h = LayoutScaler.heightFromDesign( 56 );
    const txt_x = b.w / 2 - txt_w / 2;

    const top_txt_y = fill_y + ( fill_w - txt_h ) / 2;
    const bottom_txt_y = fill_y + fill_h - ( fill_w + txt_h ) / 2;

    const top_txt = new UITextElement(
      this.ctx, txt_x, top_txt_y, txt_w, txt_h,
      {
        text: "",
        font_size: LayoutScaler.textSizeFromDesign( 44 ),
        font_weight: 800,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ),
        outline_color: ColorScheme.result_adventure_progress_outline,
        ...this.resultShadow(),
      }
    )

    bg.addChild( top_txt );
    this.adventure_progress_top_text = top_txt;

    const bottom_txt = new UITextElement(
      this.ctx, txt_x, bottom_txt_y, txt_w, txt_h,
      {
        text: "",
        font_size: LayoutScaler.textSizeFromDesign( 44 ),
        font_weight: 800,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ),
        outline_color: ColorScheme.result_adventure_progress_outline,
        ...this.resultShadow(),
      }
    )

    bg.addChild( bottom_txt );
    this.adventure_progress_bottom_text = bottom_txt;

    const goal_panel_top = this.box( 0, 1210, 1, 1 ).y;
    console.debug( "[GameResultOverlay] adventure progress layout", {
      scale: LayoutScaler.getScale(),
      visible_design: {
        width: LayoutScaler.getVisibleDesignWidth(),
        height: LayoutScaler.getVisibleDesignHeight(),
      },
      overlay: { width: this.rect.getW(), height: this.rect.getH() },
      background: { x: b.x, y: b.y, width: b.w, height: b.h, bottom: b.y + b.h },
      fill: { x: fill_x, y: fill_y, width: fill_w, height: fill_h },
      fill_absolute: {
        x: b.x + fill_x,
        y: b.y + fill_y,
        bottom: b.y + fill_y + fill_h,
      },
      labels: { top_y: top_txt_y, bottom_y: bottom_txt_y, width: txt_w, height: txt_h },
      goal_panel_top,
      gap_before_goal_panel: goal_panel_top - ( b.y + b.h ),
    } );
  }

  /** Shows the per-floor progress reported by the server for this victory. */
  public setWinStreakTowerProgress( update: WinStreakTowerProgressDto | null ): void
  {
    const cached = WinStreakTowerService.getCached();
    if ( cached?.enabled !== true )
      return;

    const cached_tower = cached.tower;
    const tower_was_already_completed = update?.new_floor === false && (
      update.tower_completed === true
      || update.wins_required === null
      || cached_tower?.completed === true
    );

    if ( !update || tower_was_already_completed || !this.adventure_progress_bg || !this.adventure_progress_fill )
      return;

    const target_floor_id = update.new_floor
      ? update.current_floor_id
      : cached_tower?.next_floor_id;
    const target_floor_index = target_floor_id === null || target_floor_id === undefined
      ? -1
      : cached_tower?.floors.findIndex( floor => floor.id === target_floor_id ) ?? -1;
    const completed_floor = update.current_floor_id === null
      ? undefined
      : cached_tower?.floors.find( floor => floor.id === update.current_floor_id );
    const wins_required = update.new_floor
      ? completed_floor?.wins_required ?? update.wins_required
      : update.wins_required;

    if ( wins_required === undefined || wins_required === null || wins_required <= 0 )
      return;

    // The windows show the cumulative level thresholds of the current and
    // next tower floors: 4 -> 7, then 7 -> 10. They are not live progress.
    const current_floor_level = target_floor_index > 0 && cached_tower
      ? cached_tower.floors
        .slice( 0, target_floor_index )
        .reduce( ( total, floor ) => total + floor.wins_required, 0 )
      : 0;
    const next_floor_level = current_floor_level + wins_required;
    const current_wins = update.new_floor ? wins_required : update.wins_progress;
    const previous_wins = Math.max( 0, current_wins - ( update.changed ? 1 : 0 ) );

    const clamp = ( value: number ): number => Math.max( 0, Math.min( 1, value ) );
    const to_fraction = clamp( current_wins / wins_required );
    const from_fraction = clamp( previous_wins / wins_required );

    console.debug( "[GameResultOverlay] adventure progress update", {
      update,
      cached_tower,
      target_floor_id,
      target_floor_index,
      wins_required,
      current_floor_level,
      next_floor_level,
      current_wins,
      previous_wins,
      from_fraction,
      to_fraction,
    } );

    this.adventure_progress_top_text?.setText( next_floor_level.toString() );
    this.adventure_progress_bottom_text?.setText( current_floor_level.toString() );
    this.adventure_progress_bg.setOpacityImmediate( 1 );
    this.adventure_progress_fill.setRevealFromBottom( from_fraction );
    const fill_tr = new TransformValue(
      from_fraction, to_fraction,
      performance.now() + GameResultOverlay.ADVENTURE_FILL_DELAY_MS, GameResultOverlay.ADVENTURE_FILL_MS,
      value =>
      {
        this.adventure_progress_fill?.setRevealFromBottom( value );
      },
      { easing: Easing.EaseOutQuad }
    );
    fill_tr.setDomSample( value => ( { reveal: value } ) );
    this.adventure_progress_fill.addTransform( fill_tr );
    this.invalidateSubtree();
  }

  private createStars(): void
  {
    const params = this.params as GameResultOverlayParams;
    if ( params.daily_challenge_num !== undefined )
      return;

    const requested_stars = params?.stars ?? 0;
    const lit = Math.max( 0, Math.min( 3, requested_stars ) );

    const STAR_D = GameResultOverlay.STAR_D_DESIGN;
    const slots: Array<{ x: number; y: number; tilt: number }> = [
      {
        x: GameResultOverlay.LEFT_STAR_X_DESIGN,
        y: GameResultOverlay.LEFT_STAR_Y_DESIGN,
        tilt: -15,
      },
      {
        x: GameResultOverlay.MIDDLE_STAR_X_DESIGN,
        y: GameResultOverlay.MIDDLE_STAR_Y_DESIGN,
        tilt: 0,
      },
      {
        x: GameResultOverlay.RIGHT_STAR_X_DESIGN,
        y: GameResultOverlay.RIGHT_STAR_Y_DESIGN,
        tilt: 15,
      },
    ];

    const debug_slots: Array<{
      index: number;
      design: { x: number; y: number; w: number; h: number; rotation: number };
      rendered: { x: number; y: number; w: number; h: number };
      visible: boolean;
    }> = [];

    slots.forEach( ( slot, i ) =>
    {
      const b = this.box(
        slot.x,
        slot.y + GameResultOverlay.HEADER_OFFSET_Y_DESIGN,
        STAR_D,
        STAR_D
      );
      debug_slots.push( {
        index: i + 1,
        design: {
          x: slot.x,
          y: slot.y + GameResultOverlay.HEADER_OFFSET_Y_DESIGN,
          w: STAR_D,
          h: STAR_D,
          rotation: slot.tilt,
        },
        rendered: { x: b.x, y: b.y, w: b.w, h: b.h },
        visible: i < lit,
      } );

      if ( i >= lit ) return;

      const star = new UIImage( this.ctx, b.x, b.y, b.w, b.h, ImgID.UI_ResultStar );
      this.addChild( star );
      this.animateStarIn( star, slot.tilt, i );
    } );

    console.debug( "[GameResultOverlay] star layout", {
      requested_stars,
      rendered_stars: lit,
      banner_design: { x: 22, y: 141, w: 1036, h: 503 },
      slots: debug_slots,
    } );
  }

  private animateStarIn( star: UIImage, tilt_deg: number, index: number ): void
  {
    const deg2rad = ( d: number ) => d * Math.PI / 180;
    const tilt = deg2rad( tilt_deg );
    const spin_from = tilt - deg2rad( GameResultOverlay.STAR_SPIN_DEG );

    star.enableRotation();
    star.setRotation( spin_from );
    star.setScale( new Vector2( GameResultOverlay.STAR_START_SCALE, GameResultOverlay.STAR_START_SCALE ) );
    star.setOpacity( 0 );

    const pop_at = performance.now()
      + GameResultOverlay.STAR_BASE_DELAY_MS
      + index * GameResultOverlay.STAR_STAGGER_MS;
    const settle_at = pop_at + GameResultOverlay.STAR_POP_MS;

    star.addTransform( new TransformOpacity( star, true, 0, 1, pop_at, GameResultOverlay.STAR_POP_MS, { easing: Easing.EaseOutQuad } ) );
    star.addTransform( new TransformRotate( star, true, spin_from, tilt, pop_at, GameResultOverlay.STAR_POP_MS, { easing: Easing.EaseOutQuad } ) );
    star.addTransform( new TransformScale( star, true, null, new Vector2( GameResultOverlay.STAR_POP_SCALE, GameResultOverlay.STAR_POP_SCALE ), pop_at, GameResultOverlay.STAR_POP_MS, { easing: Easing.EaseOutQuad } ) );
    star.addTransform( new TransformScale( star, true, null, new Vector2( 1, 1 ), settle_at, GameResultOverlay.STAR_SETTLE_MS, { easing: Easing.EaseInOutQuad } ) );
  }

  private resultShadow(): { shadow_blur: number; shadow_color: string; shadow_offset_x: number; shadow_offset_y: number }
  {
    return {
      shadow_blur: LayoutScaler.widthFromDesign( 6 ),
      shadow_color: RESULT_SHADOW_COLOR,
      shadow_offset_x: LayoutScaler.xFromDesign( 0 ),
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
    };
  }

  private createResultText(): void
  {
    const params = this.params as GameResultOverlayParams;
    const is_challenge = params?.daily_challenge_num !== undefined;

    const add_y = is_challenge ? 130 : 0;

    const b = this.box(
      208,
      385 + GameResultOverlay.HEADER_OFFSET_Y_DESIGN + add_y,
      664,
      117
    );

    const text = new UITextElement(
      this.ctx, b.x, b.y, b.w, b.h,
      {
        localization_key: params?.title_key ?? "RESULT.LEVEL_PASSED",
        font_size: is_challenge ? LayoutScaler.textSizeFromDesign( 80 ) : LayoutScaler.textSizeFromDesign( 60 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
        outline_color: this.resultTitleOutlineColor( is_challenge ),
        auto_wrap: false,
        arc_radius: is_challenge ? undefined : LayoutScaler.widthFromDesign( GameResultOverlay.RESULT_TEXT_ARC_RADIUS_DESIGN ),
        letter_spacing: LayoutScaler.widthFromDesign( 4 ),
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 44 ),
        ...this.resultShadow(),
      }
    );
    this.addChild( text );
  }

  public setRewards( rewards: ResultReward[] ): void
  {
    if ( this.is_closed ) return;

    for ( const element of this.reward_elements )
      this.removeChild( element );

    this.reward_elements = [];
    this.reward_amount_texts = [];
    this.reward_title_text = undefined;

    this.reward_glow_controller?.stop();
    this.reward_glow_controller = null;

    ( this.params as GameResultOverlayParams ).rewards = rewards;

    this.createReward();
    this.invalidateSubtree();
  }

  private createReward(): void
  {
    if ( this.is_closed ) return;

    const params = this.params as GameResultOverlayParams;
    const rewards = params?.rewards ?? [];
    if ( rewards.length === 0 ) return;

    const is_daily_challenge = params.daily_challenge_num !== undefined;
    const show_tournament_crowns = this.isTournamentResult();

    // Keep the regular reward title near the lower part of the tower roof.
    const title_b = this.box( 208, is_daily_challenge ? 690 : show_tournament_crowns ? 590 : 700, 664, 67 );
    const title = new UITextElement(
      this.ctx, title_b.x, title_b.y, title_b.w, title_b.h,
      {
        localization_key: params?.reward_title_key ?? "RESULT.REWARD",
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    this.addChild( title );
    this.reward_elements.push( title );
    this.reward_title_text = title;

    const add_y = is_daily_challenge ? 40 : 0;

    const MAX_REWARD_COLUMNS = 3;
    const CARD_GAP_X_DESIGN = 48;
    const CARD_GAP_Y_DESIGN = 32;
    const CARD_START_Y_DESIGN = ( show_tournament_crowns ? 725 : 835 ) + add_y;
    const overlay_absolute = this.getAbsolutePosition();

    rewards.forEach( ( reward, i ) =>
    {
      const row = Math.floor( i / MAX_REWARD_COLUMNS );
      const column = i % MAX_REWARD_COLUMNS;
      const items_in_row = Math.min(
        MAX_REWARD_COLUMNS,
        rewards.length - row * MAX_REWARD_COLUMNS
      );
      const row_width_design = items_in_row * RewardFrame.DESIGN_W
        + ( items_in_row - 1 ) * CARD_GAP_X_DESIGN;
      const card_x_design = DESIGN_CX - row_width_design / 2
        + column * ( RewardFrame.DESIGN_W + CARD_GAP_X_DESIGN );
      const card_y_design = CARD_START_Y_DESIGN
        + row * ( RewardFrame.DESIGN_H + CARD_GAP_Y_DESIGN );
      const center_dx = card_x_design + RewardFrame.DESIGN_W / 2;

      // The reward glow follows the same grid cell as the icon.
      const icon_b = this.box( center_dx - 108, card_y_design + 21, 216, 216 );

      if ( reward.currency === "coin" && !is_daily_challenge )
      {
        this.reward_glow_controller = Vfx2DManager.addCoinCounterGlow(
          new Vector2( overlay_absolute.x + icon_b.x + icon_b.w / 2, overlay_absolute.y + icon_b.y + icon_b.h / 2 ),
          LayoutScaler.widthFromDesign( 115 ),
          performance.now(),
          0,
          true,
          GameResultOverlay.APPEAR_MS,
          true,
          0.45
        );
      }

      const is_coin = reward.currency === "coin";
      const icon_w_design = is_coin ? 210 : 174;
      const icon_h_design = is_coin ? 168 : 174;
      const icon_w = LayoutScaler.widthFromDesign( icon_w_design );
      const icon_h = LayoutScaler.heightFromDesign( icon_h_design );
      const card_b = this.box(
        card_x_design,
        card_y_design,
        RewardFrame.DESIGN_W,
        RewardFrame.DESIGN_H
      );
      const frame = new RewardFrame(
        this.ctx,
        card_b.x,
        card_b.y,
        {
          icon_id: is_coin ? ImgID.UI_ShopBlockImg : GameResultOverlay.currencyImage( reward.currency ),
          amount_text: isTimedRewardType( reward.currency )
            ? rewardAmountText( reward.currency, reward.amount )
            : `${reward.amount}`,
          icon_width: icon_w,
          icon_height: icon_h,
        }
      );
      RewardTooltip.attachLegacy( frame, reward.currency );
      this.addChild( frame );
      this.reward_elements.push( frame );
      if ( reward.currency === "coin" )
        this.reward_amount_texts.push( frame.amount );
    } );

    if ( show_tournament_crowns )
      this.createTournamentCrownsReward( params );
  }

  private createTournamentCrownsReward( params: GameResultOverlayParams ): void
  {
    const difficulty = params.difficulty
      ?? ( params.level !== undefined ? getLevelDifficulty( params.level ) : PlayDialogDifficulty.Common );
    const count = TournamentService.crownsForWin( difficulty );
    const icon_b = this.box( DESIGN_CX - 48, 1055, 96, 80 );
    const icon = new UIImage( this.ctx, icon_b.x, icon_b.y, icon_b.w, icon_b.h, ImgID.UI_TournamentCrownIcon );
    icon.setClickable( false );
    this.addChild( icon );
    this.reward_elements.push( icon );

    const text_b = this.box( DESIGN_CX - 60, 1133, 120, 48 );
    const text = new UITextElement( this.ctx, text_b.x, text_b.y, text_b.w, text_b.h, {
      text: String( count ),
      font_size: LayoutScaler.textSizeFromDesign( 42 ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 7 ),
      outline_color: "#17204F",
    } );
    text.setClickable( false );
    this.addChild( text );
    this.reward_elements.push( text );
  }

  private createRewardButtons(): void
  {
    const params = this.params as GameResultOverlayParams;
    const x2_allowed = typeof params.onDoubleReward === "function"
      && AdsConfigService.isRewardedX2Allowed( params.rewarded_level );

    this.createRewardButton( x2_allowed ? 48 : 300, ImgID.UI_GreenFrameBtn, "GET", false );
    if ( x2_allowed )
      this.createRewardButton( 552, ImgID.UI_PurpleFrameBtn, "X2.REWARD", true );
  }

  private createGoalProgress(): void
  {
    const params = this.params as GameResultOverlayParams;
    if ( params.daily_challenge_num !== undefined )
      return;
    if ( ( params.stars ?? 0 ) <= 0 )
      return;

    const completed_level = Math.max(
      1,
      Math.floor( params.rewarded_level ?? params.level ?? StatisticsStorage.load().level )
    );
    const span = LevelPackService.getFeatureProgress( completed_level );
    if ( !span )
      return;

    const target = span.target;
    const progress = span.progress;
    const is_available = progress >= target;

    const panel_left_trim = 24;
    const panel_right_trim = 8;
    const panel_b = this.box(
      50 + panel_left_trim,
      1210,
      980 - panel_left_trim - panel_right_trim,
      318
    );
    const panel = new UINineSliceElement(
      this.ctx,
      panel_b.x,
      panel_b.y,
      panel_b.w,
      panel_b.h,
      ImgID.UI_ResultGoalBg,
      "goal_bg"
    );
    this.addChild( panel );

    const title = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 52 - panel_left_trim ),
      LayoutScaler.yFromDesign( 1 ),
      LayoutScaler.widthFromDesign( 861 ),
      LayoutScaler.heightFromDesign( 166 ),
      {
        localization_key: "RESULT.UNLOCK_GOAL",
        font_size: LayoutScaler.textSizeFromDesign( 38 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        ...this.resultShadow(),
      }
    );
    panel.addChild( title, true );

    const progress_bg_h = LayoutScaler.heightFromDesign( 79 );
    const scaled_fill_h = Math.min(
      LayoutScaler.heightFromDesign( 55 ),
      progress_bg_h
    );
    const fill_h = scaled_fill_h - ( ( progress_bg_h - scaled_fill_h ) & 1 );
    const fill_y = ( progress_bg_h - fill_h ) / 2;
    const progress_bg_design_w = 730;
    const progress_fill_inset_x_design = 12;
    const progress_fill_design_w = progress_bg_design_w - progress_fill_inset_x_design * 2;
    const progress_bg_x = LayoutScaler.xFromDesign( 66 - panel_left_trim );
    const progress_bg_w = LayoutScaler.widthFromDesign( progress_bg_design_w );

    const progress_bg = new UINineSliceElement(
      this.ctx,
      progress_bg_x,
      LayoutScaler.yFromDesign( 197 ),
      progress_bg_w,
      progress_bg_h,
      ImgID.UI_ResultProgressBarBg,
      "result_pb_bg",
      undefined,
      undefined,
      false,
      true
    );
    panel.addChild( progress_bg, true );

    const fill_max_w = LayoutScaler.widthFromDesign( progress_fill_design_w );
    const fill = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( progress_fill_inset_x_design ),
      fill_y,
      is_available
        ? fill_max_w
        : Math.max( LayoutScaler.widthFromDesign( 70 ), fill_max_w * progress / target ),
      fill_h,
      is_available ? ImgID.UI_ResultProgressBarFillAvailable : ImgID.UI_ResultProgressBarFill,
      is_available ? "result_pb_fill_available" : "result_pb_fill",
      undefined,
      undefined,
      false,
      is_available
    );
    progress_bg.addChild( fill, true );

    const progress_text_w = LayoutScaler.widthFromDesign( is_available ? 450 : 190 );
    const progress_text_x = this.rect.getW2()
      - panel_b.x
      - progress_bg_x
      - progress_text_w / 2;
    const progress_text = new UITextElement(
      this.ctx,
      progress_text_x,
      LayoutScaler.yFromDesign( is_available ? 6 : 0 ),
      progress_text_w,
      progress_bg_h,
      {
        text: is_available ? undefined : `${progress}/${target}`,
        localization_key: is_available ? "RESULT.AVAILABLE" : undefined,
        font_size: LayoutScaler.textSizeFromDesign( is_available ? 42 : 48 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        scale_to_width: false,
        auto_wrap: false,
        ...this.resultShadow(),
      }
    );
    progress_bg.addChild( progress_text, true );

    // The block the bar is counting toward, not a generic prize box — same
    // table the hand-over overlay reads, so the two cannot disagree.
    const goal_icon = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 740 - panel_left_trim ),
      LayoutScaler.yFromDesign( 107 ),
      LayoutScaler.widthFromDesign( 211 ),
      LayoutScaler.heightFromDesign( 211 ),
      goalIconFor( span.unlocks )
    );
    panel.addChild( goal_icon, true );
  }

  private createRewardButton( design_x: number, image: ImgID, localization_key: string, rewarded: boolean ): void
  {
    const b = this.box( design_x, 1570, 480, 214 );
    const button = new UINineSliceElement(
      this.ctx, b.x, b.y, b.w, b.h, image, "frame_btn", undefined, undefined, true
    );

    const text_x = rewarded ? LayoutScaler.widthFromDesign( 145 ) : 0;
    const text_w = rewarded
      ? LayoutScaler.widthFromDesign( 315 )
      : b.w;
    const text = new UITextElement(
      this.ctx, text_x, 0, text_w, b.h,
      {
        localization_key,
        font_size: LayoutScaler.textSizeFromDesign( rewarded ? 39 : 48 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        center_on_cap_height: true,
        auto_wrap: false,
        outline_color: rewarded ? ColorScheme.more_lives_purple_outline : ColorScheme.more_lives_buy_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( RESULT_BTN_OUTLINE_W ),
        outline_offset_y: LayoutScaler.yFromDesign( RESULT_BTN_OUTLINE_OFFSET_Y ),
        ...TextStyleScheme.buttonShadow(),
      }
    );

    if ( rewarded )
    {
      const icon = new UIImage(
        this.ctx,
        LayoutScaler.widthFromDesign( 73 ),
        ( b.h - LayoutScaler.heightFromDesign( 81 ) ) / 2,
        LayoutScaler.widthFromDesign( 90 ),
        LayoutScaler.heightFromDesign( 81 ),
        ImgID.UI_VideoIcon
      );
      button.addChild( icon, true );
      button.addChild( text, true );
      this.video_button = button;
      this.video_button_text = text;
      this.syncVideoButtonOfflineState();
    }
    else
    {
      button.addChild( text, true );
      this.claim_button = button;
      this.claim_button_text = text;
    }

    button.setOnClick( () =>
    {
      if ( rewarded ) void this.collectDoubleReward( button );
      else void this.collectGetReward( button );
    } );
    this.addChild( button );
  }

  private async collectGetReward( button: UINineSliceElement ): Promise<void>
  {
    if ( this.reward_inflight || this.continued ) return;

    void hapticsController.vibrate( HapticPattern.LIGHT, VibrationContext.GENERAL );
    this.reward_inflight = true;
    button.setClickable( false );
    try
    {
      const params = this.params as GameResultOverlayParams;
      const ad_level = params.daily_challenge_num === undefined ? params.rewarded_level ?? params.level : undefined;
      await tryShowInterstitial( "level_win", ad_level );
    }
    catch ( e )
    {
      console.warn( "[GameResultOverlay] Interstitial ad error:", e );
    }
    finally
    {
      this.reward_inflight = false;
      button.setClickable( true );
    }

    this.continueAfterReward();
  }

  private syncVideoButtonOfflineState(): void
  {
    if ( !this.video_button ) return;

    const ads_offline = areAdsBlockedOffline();
    if ( ads_offline === this.shown_ads_offline ) return;

    this.shown_ads_offline = ads_offline;
    if ( !ads_offline ) hideOfflineAdTooltip();
    this.video_button.setImgId(
      ads_offline ? ImgID.UI_NonActiveBtn : ImgID.UI_PurpleFrameBtn,
      ads_offline ? "non_active_btn" : "frame_btn"
    );
    setButtonTextOutline( [ this.video_button_text ], !ads_offline, ColorScheme.more_lives_purple_outline );
    this.invalidateSubtree();
  }

  private async collectDoubleReward( button: UINineSliceElement ): Promise<void>
  {
    if ( this.reward_inflight || this.continued ) return;

    if ( areAdsBlockedOffline() )
    {
      showOfflineAdTooltip( button );
      return;
    }

    this.reward_inflight = true;
    button.setClickable( false );
    try
    {
      const params = this.params as GameResultOverlayParams;
      const result = await showRewarded( "level_reward_x2", params.rewarded_level );
      if ( !result.rewarded )
      {
        if ( !result.shown ) showAdUnavailableTooltip( button );
        return;
      }

      const granted_coins = await params.onDoubleReward?.( params.rewards ?? [] );
      this.enterDoubledRewardState( typeof granted_coins === "number" ? granted_coins : undefined );
    }
    catch ( e )
    {
      console.warn( "[GameResultOverlay] x2 reward failed", e );
    }
    finally
    {
      this.reward_inflight = false;
      button.setClickable( true );
    }
  }

  private enterDoubledRewardState( granted_coins?: number ): void
  {
    if ( this.is_doubled ) return;
    this.is_doubled = true;

    const params = this.params as GameResultOverlayParams;
    const coin_rewards = ( params.rewards ?? [] ).filter( r => r.currency === "coin" );
    coin_rewards.forEach( ( reward, i ) =>
    {
      // Prefer the server's actual bonus grant; fall back to a plain x2 only
      // if the caller didn't report one (keeps old callers working).
      reward.amount += granted_coins ?? reward.amount;
      const text = this.reward_amount_texts[i];
      text?.setText( `${reward.amount}` );
      text?.setTextColor( ColorScheme.result_reward_x2_text );
    } );

    if ( this.reward_title_text )
      this.reward_title_text.setText( this.reward_title_text.getText() + " x2" );

    if ( this.claim_button_text )
      this.claim_button_text.setText( this.claim_button_text.getText() + " x2" );

    if ( this.video_button )
    {
      const btn = this.video_button;
      this.video_button = undefined;
      const now = performance.now();
      btn.addTransform( new TransformOpacity( btn, true, 1, 0, now, GameResultOverlay.APPEAR_MS, { easing: Easing.EaseOutQuad } ) );
      setTimeout( () => this.removeChild( btn ), GameResultOverlay.APPEAR_MS );
    }

    if ( this.claim_button )
    {
      const b = this.box( 300, 1570, 480, 214 );
      this.claim_button.setPixelPos( new Vector2( b.x, b.y ), true );
    }

    this.invalidateSubtree();
  }

  private getCoinRewardPositions(): Vector2[]
  {
    const params = this.params as GameResultOverlayParams;
    const rewards = params?.rewards ?? [];
    if ( rewards.length === 0 ) return [];

    const ICON_D = 216;
    const GAP_D = 60;
    const icon_dy = 856;
    const total_w_d = rewards.length * ICON_D + ( rewards.length - 1 ) * GAP_D;
    const first_center_dx = DESIGN_CX - total_w_d / 2 + ICON_D / 2;

    const positions: Vector2[] = [];
    const overlay_absolute = this.getAbsolutePosition();

    rewards.forEach( ( reward, i ) =>
    {
      if ( reward.currency === "coin" )
      {
        const center_dx = first_center_dx + i * ( ICON_D + GAP_D );
        const icon_b = this.box( center_dx - ICON_D / 2, icon_dy, ICON_D, ICON_D );
        positions.push( new Vector2(
          overlay_absolute.x + icon_b.x + icon_b.w / 2,
          overlay_absolute.y + icon_b.y + icon_b.h / 2
        ) );
      }
    } );

    return positions;
  }

  private getCoinRewardAmount(): number
  {
    const params = this.params as GameResultOverlayParams;
    const rewards = params?.rewards ?? [];
    let total = 0;
    for ( const reward of rewards )
    {
      if ( reward.currency === "coin" )
        total += reward.amount;
    }
    return total;
  }

  // === [TEMP WORKAROUND: COIN FLY OVERLAY CLOSE DELAY] ===
  private continueAfterReward(): void
  {
    if ( this.continued ) return;
    this.continued = true;
    const params = this.params as GameResultOverlayParams;

    if ( params.daily_challenge_num !== undefined )
    {
      this.flyDailyChallengeRewardIcons();
      return;
    }

    const completed_level = params.rewarded_level ?? params.level;
    if ( ( params.stars ?? 0 ) > 0 && completed_level !== undefined && completed_level > 0 && GameResultOverlay.goalJustEarned( completed_level ) )
      StatisticsStorage.update( { new_goal_pending: true } );

    const coin_positions = this.getCoinRewardPositions();
    const coin_reward_amount = this.getCoinRewardAmount();

    if ( coin_reward_amount <= 0 || coin_positions.length === 0 )
    {
      this.finishRewardSequence();
      return;
    }

    // The counter the coins fly into is CoinFlightHud: one pill with its coin, on
    // a layer above every screen, so it stays put while this overlay closes and
    // the next screen builds underneath.
    const badge_animation_start = performance.now();
    const coin_fly_start = badge_animation_start + GameResultOverlay.COIN_FLY_DELAY_MS;
    CoinFlightHud.show( badge_animation_start, true );
    const coin_target = CoinFlightHud.coinTarget();

    for ( const pos of coin_positions )
      Vfx2DManager.addCoinsFly( pos, coin_target, 10, coin_fly_start );

    this.keep_coin_flight_on_close = true;
    // Stay long enough for the coins to visibly leave the reward icon on this
    // screen; the rest of the flight carries on over the next one.
    this.reward_finish_at = badge_animation_start + GameResultOverlay.COIN_OVERLAY_HOLD_MS;
  }

  // Same choreography as AdventureBoxOpenPopup.showRewards()/flyRewardsToHud():
  // a menu-screen-style HUD bar (coin/heart counters + avatar) fades in, and
  // each reward flies from its card into the matching HUD cell — coins into
  // the coin counter, hearts into the heart counter, everything else (the
  // booster types) into the avatar slot.
  private flyDailyChallengeRewardIcons(): void
  {
    const params = this.params as GameResultOverlayParams;
    const rewards = params.rewards ?? [];

    if ( rewards.length === 0 )
    {
      this.finishRewardSequence();
      return;
    }

    const now = performance.now();
    const HUD_APPEAR_MS = 180;

    const hud: AdventureRewardHudRefs = AdventureRewardHud.build( this.ctx, this, this.rect.getW() );
    hud.row.setOpacityImmediate( 0 );
    hud.row.fadeIn( HUD_APPEAR_MS, now, Easing.EaseOutQuad );

    const ICON_D = 216;
    const GAP_D = 60;
    const icon_dy = 856;
    const total_w_d = rewards.length * ICON_D + ( rewards.length - 1 ) * GAP_D;
    const first_center_dx = DESIGN_CX - total_w_d / 2 + ICON_D / 2;
    const overlay_absolute = this.getAbsolutePosition();
    const icon_size = LayoutScaler.widthFromDesign( ICON_D * 0.75 );

    let finish_at = now;
    let heart_finish = 0;
    let avatar_finish = 0;

    rewards.forEach( ( reward, i ) =>
    {
      const center_dx = first_center_dx + i * ( ICON_D + GAP_D );
      const icon_b = this.box( center_dx - ICON_D / 2, icon_dy, ICON_D, ICON_D );
      const from = new Vector2(
        overlay_absolute.x + icon_b.x + icon_b.w / 2,
        overlay_absolute.y + icon_b.y + icon_b.h / 2
      );

      if ( reward.currency === "coin" )
      {
        const count = Math.max( 1, Math.min( 8, Math.round( reward.amount / 3 ) ) );
        finish_at = Math.max( finish_at, Vfx2DManager.addCoinsFly( from, hud.coin.target, count, now ) );
        return;
      }

      const is_booster = reward.currency !== "heart";
      const target = is_booster ? hud.avatar_target : hud.heart.target;
      const count = is_booster ? 1 : Math.min( 3, reward.amount );

      for ( let n = 0; n < count; n++ )
      {
        const f = this.flyRewardIcon( reward.currency, from, target, icon_size, now + n * 60 );
        finish_at = Math.max( finish_at, f );
        if ( is_booster ) avatar_finish = Math.max( avatar_finish, f );
        else heart_finish = Math.max( heart_finish, f );
      }
    } );

    if ( heart_finish > 0 )
      Vfx2DManager.addCoinCounterImpact( hud.heart.target, icon_size, heart_finish, 280 );
    if ( avatar_finish > 0 )
      Vfx2DManager.addCoinCounterImpact( hud.avatar_target, icon_size * 1.3, avatar_finish, 280 );

    this.reward_finish_at = finish_at + GameResultOverlay.COIN_OVERLAY_CLOSE_DELAY_MS;
  }

  private flyRewardIcon( currency: ResultRewardCurrency, from: Vector2, to: Vector2, size: number, start_time: number ): number
  {
    const DURATION_MS = 620;
    const icon = new UIImage( this.ctx, from.x - size / 2, from.y - size / 2, size, size, GameResultOverlay.currencyImage( currency ) );
    this.addChild( icon );

    const side = Math.random() < 0.5 ? -1 : 1;
    const mid_x = ( from.x + to.x ) / 2 + side * ( 40 + Math.random() * 50 );
    const mid_y = Math.min( from.y, to.y ) - ( 60 + Math.random() * 50 );

    const path = ( t: number ): Vector2 =>
    {
      const inv = 1 - t;
      const x = inv * inv * from.x + 2 * inv * t * mid_x + t * t * to.x;
      const y = inv * inv * from.y + 2 * inv * t * mid_y + t * t * to.y;
      return new Vector2( x - size / 2, y - size / 2 );
    };
    const apply = ( t: number ): void => icon.setPixelPos( path( t ), true );

    const fly = new TransformValue( 0, 1, start_time, DURATION_MS, apply, { easing: Easing.EaseInQuad } );
    fly.setDomSample( t => ( { pos: path( t ) } ) );
    icon.addTransform( fly );
    icon.addTransform( new TransformScale( icon, true, new Vector2( 2, 2 ), new Vector2( 0.4, 0.4 ), start_time, DURATION_MS, { easing: Easing.EaseInQuad } ) );

    const fade = new TransformOpacity( icon, true, 1, 0, start_time + DURATION_MS * 0.75, DURATION_MS * 0.25, { easing: Easing.EaseInQuad } );
    fade.setOnFinished( () => this.removeChild( icon ) );
    icon.addTransform( fade );

    return start_time + DURATION_MS;
  }

  private finishRewardSequence(): void
  {
    this.reward_finish_at = 0;
    const params = this.params as GameResultOverlayParams;
    try
    {
      params?.onContinue?.();
    }
    catch ( error )
    {
      console.error( "[GameResultOverlay] Continue failed:", error );
    }
    finally
    {
      OverlayManager.closeOverlay();
    }
  }

  private static currencyImage( currency: ResultRewardCurrency ): ImgID
  {
    return ADVENTURE_REWARD_ICONS[ currency ];
  }

  public override getAppearMotion(): SurfaceAppearMotion
  {
    return { from_scale: 0.85, scale_up_ms: GameResultOverlay.APPEAR_MS };
  }

  public override closesOnBackButton(): boolean
  {
    return false;
  }

  private startAppearAnimation(): void
  {
    if ( OverlayManager.useDomAnimations( this ) )
      return;

    if ( OverlayManager.useCssAnimations() )
    {
      OverlayManager.animateAppearCss( this, 0.85, GameResultOverlay.APPEAR_MS );
      return;
    }

    const now = performance.now();
    this.setScale( new Vector2( 0.85, 0.85 ) );
    this.setOpacity( 0 );
    this.addTransform( new TransformScale( this, true, null, new Vector2( 1, 1 ), now, GameResultOverlay.APPEAR_MS, { easing: Easing.EaseOutQuad } ) );
    this.addTransform( new TransformOpacity( this, true, 0, 1, now, GameResultOverlay.APPEAR_MS, { easing: Easing.EaseOutQuad } ) );
  }

  public onTouchStart( p: Vector2, now: number ): boolean
  {
    if ( now < this.interactive_at || this.continued || this.reward_inflight )
      return true;
    return super.onTouchStart( p, now );
  }

  public update( now: number ): void
  {
    super.update( now );

    if ( this.reward_finish_at !== 0 && now >= this.reward_finish_at )
      this.finishRewardSequence();

    this.syncVideoButtonOfflineState();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }

  public override onOverlayWillClose(): void
  {
    this.is_closed = true;
    hideOfflineAdTooltip();
    this.stopFireworks();
    Vfx2DManager.clearConfetti();

    this.reward_glow_controller?.stop();
    this.reward_glow_controller = null;
    if ( !this.keep_coin_flight_on_close )
    {
      WebGLUIRuntime.clearAll()
      Vfx2DRuntime.clearAll();
    }
  }

  public name(): string
  {
    return "GameResultOverlay";
  }
}
